import * as React from 'react';
import {Button} from 'src/Button';
import {ImportParseError, ImportPreviewList} from 'src/ImportPreview';
import {Textarea} from 'src/inputs/Textarea';
import {parseJson} from 'src/parseJson';
import parseVenmo from 'src/parseVenmo';
import type {ExtractResult} from '@danwang/extractor';
import type Option from '@danwang/option';
import type {VenmoResult} from 'src/parseVenmo';

type Props = {
  onChange: (value: Option<VenmoResult[]>) => void;
};

export const ImportVenmo = (props: Props) => {
  const [text, setText] = React.useState('');
  const [parsed, setParsed] = React.useState<ExtractResult<VenmoResult[]>>(() =>
    parseVenmo(text),
  );

  const handleChange = (value: string) => {
    const newParsed = parseVenmo(value);
    setText(value);
    setParsed(newParsed);
    props.onChange(newParsed.toOption());
  };

  const handleFormat = () => {
    parseJson(text)
      .map((json) => JSON.stringify(json, null, 2))
      .forEach((formatted) => setText(formatted));
  };

  let preview: React.ReactNode = null;
  if (parsed.isSuccess) {
    preview = (
      <ImportPreviewList
        rows={parsed.successValue.map((t) => ({
          id: t.venmoId,
          date: t.date,
          description: t.description,
          amount: t.amount,
        }))}
      />
    );
  } else if (text.trim() !== '') {
    // An empty textarea trivially fails to parse; that is the resting state,
    // not an error worth surfacing.
    preview = (
      <ImportParseError sourceName="Venmo" error={parsed.failureValue} />
    );
  }

  return (
    <div className="flex flex-col gap-3">
      <div className="flex flex-col gap-2">
        <Textarea
          value={text}
          onChange={handleChange}
          placeholder="Paste the JSON transaction feed from your Venmo account…"
          spellCheck={false}
          className="h-36 resize-y font-mono text-xs"
        />
        <div className="flex justify-end">
          <Button
            variant="outline"
            className="px-3 py-1 text-sm"
            onClick={handleFormat}
            disabled={text.trim() === ''}
          >
            Format JSON
          </Button>
        </div>
      </div>
      {preview}
    </div>
  );
};
